import React, { useState } from 'react';
import { adminAPI } from '../../services/api';
import { WorkdaysSelector } from './WorkdaysSelector';
import './UserEditModal.css';
import { Clock,Edit, X, Pencil, Laptop, SmartphoneNfc, 
  Lock, Save, ClipboardClock, Lightbulb, Key
 } from 'lucide-react';

interface UserEditModalProps {
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    vacationDays: number;
    weeklyHours: number;
    workDays: string;
    maxWorkHours?: number; 
    startDate: string;
    nfcId?: string | null;
    webTimeTrackingEnabled?: boolean;
    isActive: boolean;
    twoFactorEnabled?: boolean;
    initialOvertimeMinutes?: number;
  };
  onClose: () => void;
  onUserUpdated: () => void;
}

export const UserEditModal: React.FC<UserEditModalProps> = ({ 
  user, 
  onClose, 
  onUserUpdated 
}) => {
  // Helper functions to convert between minutes and hours:minutes
  const minutesToHoursMinutes = (totalMinutes: number) => {
    const isNegative = totalMinutes < 0;
    const absMinutes = Math.abs(totalMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;
    return {
      hours: isNegative ? -hours : hours,
      minutes: minutes
    };
  };

  const hoursMinutesToMinutes = (hoursStr: string, minutesStr: string) => {
    const hours = parseInt(hoursStr) || 0;
    const minutes = parseInt(minutesStr) || 0;
    
    // If hours are negative, the result is negative
    if (hours < 0) {
      return -(Math.abs(hours) * 60 + Math.abs(minutes));
    } else {
      return hours * 60 + Math.abs(minutes);
    }
  };

  const initialOvertime = minutesToHoursMinutes(user.initialOvertimeMinutes || 0);

  const [formData, setFormData] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    role: user.role,
    vacationDays: user.vacationDays,
    weeklyHours: user.weeklyHours,
    workDays: user.workDays,
    maxWorkHours: user.maxWorkHours || 12,
    startDate: user.startDate && !isNaN(new Date(user.startDate).getTime())
      ? new Date(user.startDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    nfcId: user.nfcId || '',
    webTimeTrackingEnabled: user.webTimeTrackingEnabled !== undefined ? user.webTimeTrackingEnabled : true,
    isActive: user.isActive,
    initialOvertimeHours: initialOvertime.hours.toString(),
    initialOvertimeMinutes: initialOvertime.minutes.toString()
  });
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  //const [show2FASection, setShow2FASection] = useState(false);

  const roles = ['ADMIN', 'MANAGER', 'ACCOUNTING', 'EMPLOYEE'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Convert hours and minutes back to total minutes
      const totalOvertimeMinutes = hoursMinutesToMinutes(
        formData.initialOvertimeHours, 
        formData.initialOvertimeMinutes
      );

      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        vacationDays: Number(formData.vacationDays),
        weeklyHours: Number(formData.weeklyHours),
        maxWorkHours: Number(formData.maxWorkHours),
        startDate: formData.startDate,
        workDays: String(formData.workDays),
        nfcId: formData.nfcId || null,
        webTimeTrackingEnabled: formData.webTimeTrackingEnabled,
        isActive: formData.isActive,
        initialOvertimeMinutes: totalOvertimeMinutes
      };

      console.log('🔄 Sending update request with data:', updateData);
      console.log('🕐 Overtime conversion:', {
        hoursInput: formData.initialOvertimeHours,
        minutesInput: formData.initialOvertimeMinutes,
        totalMinutes: totalOvertimeMinutes,
        formatted: formatMinutes(totalOvertimeMinutes)
      });

      await adminAPI.updateUserData(user.id, updateData);

      setMessage({ 
        type: 'success', 
        text: 'Benutzerdaten erfolgreich aktualisiert. Sie können das Fenster schließen oder weitere Änderungen vornehmen.' 
      });
      
      onUserUpdated();

    } catch (error: any) {
      console.error('❌ Error updating user:', error);
      console.error('❌ Error response:', error.response?.data);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Aktualisieren der Benutzerdaten'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Passwort muss mindestens 6 Zeichen lang sein' });
      return;
    }

    if (!confirm(`Passwort für ${user.firstName} ${user.lastName} wirklich zurücksetzen?`)) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await adminAPI.resetUserPassword(user.id, newPassword);
      setMessage({ type: 'success', text: 'Passwort erfolgreich zurückgesetzt' });
      setNewPassword('');
      setTimeout(() => {
        setShowPasswordReset(false);
      }, 1500);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Zurücksetzen des Passworts'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset2FA = async () => {
    if (!user.twoFactorEnabled) {
      setMessage({ type: 'error', text: 'Benutzer hat 2FA nicht aktiviert' });
      return;
    }

    if (!confirm(`2FA für ${user.firstName} ${user.lastName} wirklich zurücksetzen?\n\nDies wird:\n- 2FA deaktivieren\n- Alle Backup-Codes löschen\n- Den Benutzer dazu zwingen, 2FA neu einzurichten`)) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await adminAPI.reset2FA(user.id);
      setMessage({ 
        type: 'success', 
        text: `2FA für ${user.firstName} ${user.lastName} erfolgreich zurückgesetzt. Der Benutzer kann 2FA jetzt neu einrichten.` 
      });
      onUserUpdated();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Zurücksetzen von 2FA'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'initialOvertimeHours') {
      // Allow negative numbers for hours, improve validation
      let numValue = value.replace(/[^-0-9]/g, ''); // Allow only digits and minus
      
      // Ensure minus can only be at the beginning
      if (numValue.includes('-')) {
        const minusCount = (numValue.match(/-/g) || []).length;
        if (minusCount > 1) {
          // Remove extra minus signs
          numValue = numValue.replace(/-/g, '');
          if (value.startsWith('-')) {
            numValue = '-' + numValue;
          }
        } else if (!numValue.startsWith('-') && numValue.includes('-')) {
          // Move minus to the beginning
          numValue = '-' + numValue.replace('-', '');
        }
      }
      
      // Limit to reasonable values (-999 to 999 hours)
      const numericValue = parseInt(numValue) || 0;
      if (Math.abs(numericValue) <= 999) {
        setFormData(prev => ({ ...prev, [name]: numValue }));
      }
    } else if (name === 'initialOvertimeMinutes') {
      // Only allow positive numbers for minutes (0-59)
      const numValue = value.replace(/[^0-9]/g, '');
      const minutes = parseInt(numValue) || 0;
      if (minutes <= 59) {
        setFormData(prev => ({ ...prev, [name]: numValue }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    }
  };

  const handleWorkdaysChange = (workDays: string) => {
    setFormData(prev => ({
      ...prev,
      workDays: workDays
    }));
  };

  const formatWorkDays = (workDays: string) => {
    if (!workDays) return 'Mo-Fr';
    
    const dayMapping: { [key: string]: string } = {
      '1': 'Mo',
      '2': 'Di', 
      '3': 'Mi',
      '4': 'Do',
      '5': 'Fr',
      '6': 'Sa',
      '7': 'So'
    };
    
    const days = workDays.split(',').map(day => dayMapping[day.trim()]).filter(Boolean);
    return days.join(', ');
  };

  const formatMinutes = (minutes: number) => {
    if (minutes === 0) return '0:00';
    const hours = Math.floor(Math.abs(minutes) / 60);
    const mins = Math.abs(minutes) % 60;
    const sign = minutes < 0 ? '-' : '';
    return `${sign}${hours}:${mins.toString().padStart(2, '0')}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3><Edit /> Benutzer bearbeiten</h3>
          <div className="modal-header-actions">
            <button 
              type="submit" 
              form="user-edit-form"
              className="btn primary" 
              disabled={loading}
            >
              {loading ? 'Speichert...' : <><Save />Speichern</>}
            </button>
            <button className="modal-close" onClick={onClose}><X /></button>
          </div>
        </div>

        <div className="modal-body">
          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          <form id="user-edit-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Vorname:</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required />
              </div>
              <div className="form-group">
                <label>Nachname:</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required 
                />
              </div>
            </div>

            <div className="form-group">
              <label>E-Mail:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required 
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Rolle:</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  {roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Status:</label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange} 
                  />
                  Aktiv
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Urlaubstage pro Jahr:</label>
                <input
                  type="number"
                  name="vacationDays"
                  value={formData.vacationDays}
                  onChange={handleChange}
                  min="0"
                  max="365"
                  required 
                />
              </div>
              <div className="form-group">
                <label>Wochenstunden:</label>
                <input
                  type="number"
                  name="weeklyHours"
                  value={formData.weeklyHours}
                  onChange={handleChange}
                  min="0"
                  max="80"
                  step="0.5"
                  required 
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Maximale Arbeitszeit pro Tag (Stunden):</label>
                <input
                  type="number"
                  step="0.5"
                  name="maxWorkHours"
                  value={formData.maxWorkHours}
                  onChange={handleChange}
                  min="1"
                  max="24"
                  required
                />
                <small>Nach dieser Zeit erfolgt automatisches Ausstempeln</small>
              </div>

              <div className="form-group">
                <label>Startdatum:</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>NFC ID:</label>
              <input
                type="text"
                name="nfcId"
                value={formData.nfcId}
                onChange={handleChange}
                placeholder="NFC Karten-ID (optional)"
                maxLength={50} />
              <small style={{ color: '#666', fontSize: '0.875rem' }}>
                <SmartphoneNfc /> Eindeutige NFC-Karten ID für kontaktlose Zeiterfassung
              </small>
            </div>

            <div className="form-group">
              <label>Website-Zeiterfassung:</label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="webTimeTrackingEnabled"
                  checked={formData.webTimeTrackingEnabled}
                  onChange={handleChange} />
                Zeiterfassung über Website erlauben
              </label>
              <small style={{ color: '#666', fontSize: '0.875rem' }}>
                {formData.webTimeTrackingEnabled
                  ? <><Laptop /> Benutzer kann über Website ein- und ausstempeln</>
                  : <><SmartphoneNfc /> Benutzer kann nur über NFC-Karte ein- und ausstempeln</>}
              </small>
            </div>

            <div className="form-group">
              <label>Arbeitstage pro Woche:</label>
              <WorkdaysSelector
                selectedDays={formData.workDays}
                onChange={handleWorkdaysChange}
                disabled={loading} />
              <small style={{ color: '#666', fontSize: '0.875rem' }}>
                Bestimmt, welche Wochentage als Arbeitstage gelten. Die Gesamtwochenstunden bleiben gleich.
                <br />
                <strong>Aktuell: {formatWorkDays(formData.workDays)}</strong>
              </small>
            </div>

            <div className="form-group">
              <label>Initial Über-/Unterstunden:</label>
              <div className="time-input-group" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input
                    type="text"
                    name="initialOvertimeHours"
                    value={formData.initialOvertimeHours}
                    onChange={handleChange}
                    style={{ width: '80px' }}
                    placeholder="0"
                    maxLength={4}
                  />
                  <span>Std</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input
                    type="text"
                    name="initialOvertimeMinutes"
                    value={formData.initialOvertimeMinutes}
                    onChange={handleChange}
                    style={{ width: '80px' }}
                    placeholder="0"
                    maxLength={2}
                  />
                  <span>Min</span>
                </div>
                <span style={{ marginLeft: '10px', color: '#666', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  = {formatMinutes(hoursMinutesToMinutes(
                    formData.initialOvertimeHours || '0', 
                    formData.initialOvertimeMinutes || '0'
                  ))}
                </span>
              </div>
              <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '8px' }}>
                <Clock /> Startwert für Über-/Unterstunden aus vorherigen Systemen<br />
                <Lightbulb /> Beispiele: <strong>2 Std 30 Min</strong> = +2:30 Überstunden, <strong>-2 Std 30 Min</strong> = -2:30 Unterstunden<br />
                <Pencil /> Für Unterstunden bei den Stunden ein <strong>Minus (-)</strong> eingeben<br />
                <ClipboardClock /> Erlaubt: -999 bis +999 Stunden, 0 bis 59 Minuten
              </small>
            </div>

            {/* Optional: Password Reset & 2FA Section */}
            {user.twoFactorEnabled && (
              <div className="additional-actions">
                <h4><Lock /> Zusätzliche Aktionen</h4>
                <button
                  type="button"
                  className="btn warning"
                  onClick={handleReset2FA}
                  disabled={loading}
                >
                  2FA zurücksetzen
                </button>
              </div>
            )}

            <div className="additional-actions">
              <h4><Key /> Passwort zurücksetzen</h4>
              {!showPasswordReset ? (
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => setShowPasswordReset(true)}
                >
                  Passwort zurücksetzen
                </button>
              ) : (
                <form onSubmit={handlePasswordReset}>
                  <div className="form-group">
                    <label>Neues Passwort:</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mindestens 6 Zeichen"
                      minLength={6}
                      required
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn secondary"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setNewPassword('');
                      }}
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      className="btn primary"
                      disabled={loading}
                    >
                      {loading ? 'Setzt zurück...' : 'Passwort setzen'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserEditModal;